<template>
  <div class="de-table-container">
    <!-- Data Table -->
    <div class="table-wrapper">
      <el-table
        ref="deTable"
        :data="paginatedData"
        border
        :height="tableHeight"
        style="width: 100%"
        @sort-change="handleSortChange"
        @selection-change="handleSelectionChange"
      >
        <el-table-column
          type="selection"
          width="55"
          :selectable="checkSelectable"
        >
        </el-table-column>
        
        <el-table-column
          prop="geneSymbol"
          label="Protein"
          sortable="custom"
          show-overflow-tooltip
        >
        </el-table-column>

        <el-table-column
          prop="contrast"
          label="Contrast"
          sortable="custom"
          show-overflow-tooltip
        >
        </el-table-column>

        <el-table-column
          prop="log2FC"
          label="log2FC"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">
              {{ formatNumber(row.log2FC, 3) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="pval"
          label="P-value"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatScientific(row.pval) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="FDR"
          label="FDR"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatScientific(row.FDR) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Pagination & Download -->
    <div class="pagination-bar">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      >
      </el-pagination>
      <el-button 
        size="small" 
        type="primary" 
        icon="el-icon-download"
        :loading="downloading"
        @click="downloadTable"
        class="download-btn"
      >
        Download CSV
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DifferentialExpressionTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: [Number, String],
      default: '100%'
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 20,
      sortProp: 'FDR',
      sortOrder: 'ascending',
      selectedGenes: [],
      downloading: false
    }
  },
  watch: {
    paginatedData() {
      // When data changes, maintain selection state
      this.$nextTick(() => {
        this.syncTableSelection();
      });
    }
  },
  computed: {
    // Sort data
    sortedData() {
      if (!this.data || this.data.length === 0) {
        return []
      }
      
      const data = [...this.data]
      
      if (this.sortProp) {
        data.sort((a, b) => {
          let aVal = a[this.sortProp]
          let bVal = b[this.sortProp]
          
          // Handle numeric sorting
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return this.sortOrder === 'ascending' ? aVal - bVal : bVal - aVal
          }
          
          // Handle string sorting
          aVal = String(aVal || '')
          bVal = String(bVal || '')
          
          if (this.sortOrder === 'ascending') {
            return aVal.localeCompare(bVal)
          } else {
            return bVal.localeCompare(aVal)
          }
        })
      }
      
      return data
    },
    
    // Paginate data
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.sortedData.slice(start, end)
    },
    
    total() {
      return this.sortedData.length
    }
  },
  methods: {
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      this.currentPage = 1
    },
    
    handleSelectionChange(selectedRows) {
      // Extract gene symbols from selected rows
      const selectedGenes = selectedRows.map(row => row.geneSymbol).filter(Boolean)
      this.selectedGenes = selectedGenes
      this.$emit('selection-change', selectedGenes)
    },
    
    checkSelectable() {
      // All rows are selectable
      return true
    },
    
    syncTableSelection() {
      // Sync checkbox state with current selection
      if (!this.$refs.deTable) return
      
      this.$refs.deTable.clearSelection()
      
      this.paginatedData.forEach(row => {
        if (this.selectedGenes.includes(row.geneSymbol)) {
          this.$refs.deTable.toggleRowSelection(row, true)
        }
      })
    },
    
    formatNumber(value, decimals = 3) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A'
      return Number(value).toFixed(decimals)
    },
    
    formatScientific(value) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A'
      const num = Number(value)
      return num < 0.001 ? num.toExponential(2) : num.toFixed(3)
    },
    
    downloadTable() {
      // Download the current filtered data as CSV (client-side)
      if (!this.sortedData || this.sortedData.length === 0) {
        this.$message.warning('No data to download')
        return
      }
      
      this.downloading = true
      
      try {
        // Create CSV content
        const headers = ['Protein', 'Contrast', 'log2FC', 'P-value', 'FDR']
        const rows = this.sortedData.map(row => [
          row.geneSymbol || '',
          row.contrast || '',
          row.log2FC != null ? row.log2FC : '',
          row.pval != null ? row.pval : '',
          row.FDR != null ? row.FDR : ''
        ])
        
        // Build CSV string
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => {
            // Escape cells that contain commas or quotes
            const str = String(cell)
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`
            }
            return str
          }).join(','))
        ].join('\n')
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `differential_expression_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        this.$message.success(`Downloaded ${this.sortedData.length} rows`)
      } catch (error) {
        console.error('Download failed:', error)
        this.$message.error('Failed to download table')
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.de-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  padding: 8px;
}

.numeric-cell {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.download-btn {
  margin-left: auto;
}

/* Table styling enhancements */
::v-deep .el-table th {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  font-weight: 600;
  font-size: 13px;
}

::v-deep .el-table td {
  font-size: 13px;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafafa;
}

::v-deep .el-table__body tr:hover > td {
  background-color: #f0f7ff !important;
}
</style>

